import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import DeclassementBadge from "@/components/DeclassementAlertBadge";
import { batimentColor } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, ReferenceLine, CartesianGrid,
} from "recharts";
import { FileSpreadsheet, CalendarDays, TrendingDown, TrendingUp, Minus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function BilanAnnuel() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/bilan/annuel", { params: { year } }).then((r) => setData(r.data));
  }, [year]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/bilan/annuel/export-csv", { params: { year }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bilan_annuel_autheuil_${year}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export CSV annuel téléchargé");
    } catch (e) {
      toast.error("Échec de l'export");
    }
  };

  if (!data) return <div className="text-slate-400">Chargement…</div>;

  const years = data.years.includes(year) ? data.years : [year, ...data.years];
  const total = data.annee.P.nb + data.annee.D.nb;
  const chart = data.months.map((m) => ({
    mois: m.label, Conforme: m.P.nb, Déclassé: m.D.nb,
    Taux: m.P.nb + m.D.nb ? m.taux : null,
  }));
  const cell = (c) => (c.nb ? c.nb : "—");

  return (
    <div className="space-y-5" data-testid="bilan-annuel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/bilan" data-testid="bilan-annuel-back-link" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-slate-500 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" /> Bilan mensuel
          </Link>
          <h1 className="font-heading font-black text-3xl uppercase tracking-tight text-slate-900">Bilan annuel</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger data-testid="bilan-annuel-year-select" className="h-12 w-32 font-mono font-bold">
              <CalendarDays className="w-4 h-4 mr-2 text-slate-400" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={String(y)} data-testid={`bilan-annuel-year-${y}`}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="bilan-annuel-export-csv-btn" variant="outline" onClick={exportCsv} className="h-12 font-bold">
            <FileSpreadsheet className="w-5 h-5 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testId="annuel-kpi-total" label="Palettes produites" value={total} sub={`${(data.annee.P.net + data.annee.D.net).toFixed(1)} kg net`} />
        <Kpi testId="annuel-kpi-conforme" label="Conformes (P)" value={data.annee.P.nb} sub={`${data.annee.P.net.toFixed(1)} kg`} tone="emerald" />
        <Kpi testId="annuel-kpi-declassee" label="Déclassées" value={data.annee.D.nb} sub={`${data.annee.D.net.toFixed(1)} kg`} tone="rose" />
        <div data-testid="annuel-kpi-taux" className="rounded-2xl bg-white border-2 border-slate-200 p-5">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-2">Taux déclassement {year}</div>
          <DeclassementBadge taux={data.annee.taux} seuil={data.seuil} />
          <Tendance value={data.tendance} />
        </div>
      </div>

      {/* Chart */}
      <div className="rounded-2xl bg-white border-2 border-slate-200 p-5">
        <h2 className="font-heading font-bold text-xl uppercase tracking-tight text-slate-900 mb-1">
          12 mois — production et tendance du taux de déclassement
        </h2>
        <p className="text-xs text-slate-400 font-semibold mb-4">Barres : palettes par mois · Courbe : taux de déclassement (%) · Pointillé : seuil {data.seuil} %</p>
        <div style={{ width: "100%", height: 300 }} data-testid="annuel-chart">
          <ResponsiveContainer>
            <ComposedChart data={chart}>
              <CartesianGrid stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="mois" fontSize={11} />
              <YAxis yAxisId="nb" fontSize={11} allowDecimals={false} />
              <YAxis yAxisId="taux" orientation="right" fontSize={11} unit="%" domain={[0, (max) => Math.max(10, Math.ceil(max / 10) * 10)]} />
              <Tooltip formatter={(v, n) => (n === "Taux" ? `${v} %` : v)} />
              <Legend />
              <ReferenceLine yAxisId="taux" y={data.seuil} stroke="#e11d48" strokeDasharray="4 4" />
              <Bar yAxisId="nb" dataKey="Conforme" stackId="a" fill="#059669" />
              <Bar yAxisId="nb" dataKey="Déclassé" stackId="a" fill="#e11d48" radius={[4, 4, 0, 0]} />
              <Line yAxisId="taux" type="linear" dataKey="Taux" stroke="#0f172a" strokeWidth={3} dot={{ r: 4 }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Table 12 months side by side */}
      <div className="rounded-2xl bg-white border-2 border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm" data-testid="annuel-table">
            <thead className="bg-slate-900 text-white">
              <tr className="text-center font-bold uppercase tracking-wide">
                <th className="px-3 py-3 text-left sticky left-0 bg-slate-900">Bâtiment</th>
                {data.months.map((m) => (
                  <th key={m.month} className={`px-2 py-3 ${data.mois_actifs.includes(m.month) ? "" : "text-slate-500"}`}>{m.label}</th>
                ))}
                <th className="px-3 py-3 bg-amber-400 text-slate-900">Année</th>
              </tr>
            </thead>
            <tbody className="font-mono text-center">
              {data.batiments.map((b) => (
                <Fragment key={b}>
                  <tr className="border-t-2 border-slate-200">
                    <td className="px-3 py-2 text-left font-body sticky left-0 bg-white">
                      <span className={`px-2 py-0.5 rounded font-heading font-black ${batimentColor(b)}`}>{b}</span>
                      <span className="ml-2 text-xs text-emerald-700 font-bold">Conforme</span>
                    </td>
                    {data.months.map((m) => <td key={m.month} data-testid={`annuel-${b}-P-${m.month}`} className="px-2 py-2">{cell(m.cells[b].P)}</td>)}
                    <td className="px-3 py-2 font-black bg-amber-50">{cell(data.annee.cells[b].P)}</td>
                  </tr>
                  <tr className="bg-rose-50/60">
                    <td className="px-3 py-2 text-left font-body sticky left-0 bg-rose-50">
                      <span className="ml-1 text-xs text-rose-700 font-bold">{b} Déclassé</span>
                    </td>
                    {data.months.map((m) => <td key={m.month} data-testid={`annuel-${b}-D-${m.month}`} className="px-2 py-2 text-rose-800">{cell(m.cells[b].D)}</td>)}
                    <td className="px-3 py-2 font-black text-rose-800 bg-amber-50">{cell(data.annee.cells[b].D)}</td>
                  </tr>
                  <tr className="text-[11px]">
                    <td className="px-3 py-1 text-left font-body text-slate-400 sticky left-0 bg-white">Taux {b}</td>
                    {data.months.map((m) => (
                      <td key={m.month} className={`px-2 py-1 ${m.taux_batiments[b] > data.seuil ? "text-rose-600 font-bold" : "text-slate-400"}`}>
                        {m.cells[b].P.nb + m.cells[b].D.nb ? `${m.taux_batiments[b].toFixed(1)}%` : ""}
                      </td>
                    ))}
                    <td className={`px-3 py-1 font-bold bg-amber-50 ${data.annee.taux_batiments[b] > data.seuil ? "text-rose-600" : "text-slate-500"}`}>
                      {data.annee.taux_batiments[b].toFixed(1)}%
                    </td>
                  </tr>
                </Fragment>
              ))}
              <tr className="bg-slate-100 font-black border-t-2 border-slate-300">
                <td className="px-3 py-3 text-left font-body uppercase sticky left-0 bg-slate-100">Total palettes</td>
                {data.months.map((m) => <td key={m.month} data-testid={`annuel-total-${m.month}`} className="px-2 py-3">{m.P.nb + m.D.nb || "—"}</td>)}
                <td className="px-3 py-3 bg-amber-100">{total}</td>
              </tr>
              <tr className="bg-slate-100 font-bold">
                <td className="px-3 py-2 text-left font-body uppercase text-xs sticky left-0 bg-slate-100">Taux déclassement</td>
                {data.months.map((m) => (
                  <td key={m.month} data-testid={`annuel-taux-${m.month}`} className={`px-2 py-2 ${m.taux > data.seuil ? "text-rose-600" : "text-emerald-700"}`}>
                    {m.P.nb + m.D.nb ? `${m.taux.toFixed(1)}%` : "—"}
                  </td>
                ))}
                <td className={`px-3 py-2 bg-amber-100 ${data.annee.taux > data.seuil ? "text-rose-600" : "text-emerald-700"}`}>{data.annee.taux.toFixed(1)}%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 text-[11px] text-slate-400 font-semibold">Nombre de palettes par mois (date de ponte) · taux en rouge au-dessus du seuil {data.seuil} %</div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, tone, testId }) {
  const color = { emerald: "text-emerald-700", rose: "text-rose-700" }[tone] || "text-slate-900";
  return (
    <div data-testid={testId} className="rounded-2xl bg-white border-2 border-slate-200 p-5">
      <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">{label}</div>
      <div className={`font-mono font-black text-4xl ${color}`}>{value}</div>
      <div className="text-xs text-slate-400 mt-1 font-semibold font-mono">{sub}</div>
    </div>
  );
}

function Tendance({ value }) {
  if (value === null || value === undefined)
    return <div className="text-xs text-slate-400 mt-2 font-semibold">Tendance : pas assez de mois</div>;
  const Icon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : Minus;
  const color = value > 0 ? "text-rose-600" : value < 0 ? "text-emerald-600" : "text-slate-500";
  return (
    <div data-testid="annuel-tendance" className={`text-xs mt-2 font-bold flex items-center gap-1 ${color}`}>
      <Icon className="w-4 h-4" /> {value > 0 ? "+" : ""}{value.toFixed(1)} pt vs mois précédent actif
    </div>
  );
}
